import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { FlagIT, FlagGB } from "@/components/Flags";

const homeSections = [
  { id: "home", key: "nav.home" },
  { id: "services", key: "nav.services" },
  { id: "about", key: "nav.about" },
  { id: "gallery", key: "nav.gallery" },
  { id: "contact", key: "nav.contact" },
];

export default function Header({ manifestoEnabled = false, bookingEnabled = false, bookingLabel = "" }) {
  const { lang, setLang, t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    if (!isHome) {
      navigate(`/#${id}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // On non-home pages, treat as "scrolled" style by default for legibility
  const dark = scrolled || !isHome;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        dark
          ? "bg-white/90 backdrop-blur-md border-b border-[color:var(--afb-cream)] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="afb-container flex items-center justify-between h-20">
        <button
          onClick={() => isHome ? handleNav("home") : navigate("/")}
          className="flex items-center gap-3 group"
          data-testid="logo-home"
        >
          <span className="inline-block w-8 h-[2px] bg-[color:var(--afb-gold)]" />
          <span
            className={`serif text-lg sm:text-xl tracking-wide transition-colors ${
              dark ? "text-afb-ink" : "text-white"
            }`}
          >
            Agenzia Funebre <span className="text-afb-gold">Barone</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {homeSections.map((s) => (
            <button
              key={s.id}
              onClick={() => handleNav(s.id)}
              className={`text-sm tracking-wide transition-colors hover:text-afb-gold ${
                dark ? "text-afb-ink" : "text-white/85"
              }`}
              data-testid={`nav-${s.id}`}
            >
              {t(s.key)}
            </button>
          ))}
          {manifestoEnabled && (
            <Link
              to="/manifesto"
              className={`text-sm tracking-wide transition-colors hover:text-afb-gold ${
                dark ? "text-afb-ink" : "text-white/85"
              }`}
              data-testid="nav-manifesto"
            >
              {t("nav.manifesto")}
            </Link>
          )}
          {bookingEnabled && (
            <Link
              to="/prenotazione"
              className={`text-sm tracking-wide transition-colors hover:text-afb-gold ${
                dark ? "text-afb-ink" : "text-white/85"
              }`}
              data-testid="nav-booking"
            >
              {bookingLabel || "Prenotazione"}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <div
            className={`hidden sm:flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${
              dark
                ? "border border-[color:var(--afb-cream)] bg-white"
                : "border border-white/20 bg-white/5 backdrop-blur"
            }`}
            data-testid="lang-switcher"
          >
            <button
              onClick={() => setLang("it")}
              className={`flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full transition ${
                lang === "it"
                  ? dark
                    ? "bg-afb-ink text-white"
                    : "bg-white text-afb-ink"
                  : dark
                    ? "text-afb-muted"
                    : "text-white/80"
              }`}
              data-testid="lang-it"
            >
              <FlagIT className="w-4 h-3 rounded-[1px] shadow-sm" />
              IT
            </button>
            <button
              onClick={() => setLang("en")}
              className={`flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full transition ${
                lang === "en"
                  ? dark
                    ? "bg-afb-ink text-white"
                    : "bg-white text-afb-ink"
                  : dark
                    ? "text-afb-muted"
                    : "text-white/80"
              }`}
              data-testid="lang-en"
            >
              <FlagGB className="w-4 h-3 rounded-[1px] shadow-sm" />
              EN
            </button>
          </div>

          <Link
            to="/admin"
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
            data-testid="header-admin-link"
          >
            {t("nav.admin")}
          </Link>

          <button
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 transition-colors ${
              dark ? "text-afb-ink" : "text-white"
            }`}
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-white border-t border-[color:var(--afb-cream)]"
          data-testid="mobile-menu"
        >
          <div className="afb-container py-6 flex flex-col gap-4">
            {homeSections.map((s) => (
              <button
                key={s.id}
                onClick={() => handleNav(s.id)}
                className="text-left text-base text-afb-ink hover:text-afb-gold py-2"
                data-testid={`mobile-nav-${s.id}`}
              >
                {t(s.key)}
              </button>
            ))}
            {manifestoEnabled && (
              <Link
                to="/manifesto"
                onClick={() => setOpen(false)}
                className="text-left text-base text-afb-ink hover:text-afb-gold py-2"
                data-testid="mobile-nav-manifesto"
              >
                {t("nav.manifesto")}
              </Link>
            )}
            {bookingEnabled && (
              <Link
                to="/prenotazione"
                onClick={() => setOpen(false)}
                className="text-left text-base text-afb-ink hover:text-afb-gold py-2"
                data-testid="mobile-nav-booking"
              >
                {bookingLabel || "Prenotazione"}
              </Link>
            )}
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => setLang("it")}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${
                  lang === "it" ? "bg-afb-ink text-white" : "border border-[color:var(--afb-cream)]"
                }`}
                data-testid="mobile-lang-it"
              >
                <FlagIT className="w-4 h-3 rounded-[1px]" />
                Italiano
              </button>
              <button
                onClick={() => setLang("en")}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${
                  lang === "en" ? "bg-afb-ink text-white" : "border border-[color:var(--afb-cream)]"
                }`}
                data-testid="mobile-lang-en"
              >
                <FlagGB className="w-4 h-3 rounded-[1px]" />
                English
              </button>
              <Link
                to="/admin"
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
                data-testid="mobile-admin-link"
              >
                {t("nav.admin")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
